import type { TFunction } from "i18next";
import "dayjs/locale/pl";

import dayjs from "dayjs";

import { CalendarEventStatus, CalendarEventType } from "@fitness-app/data-models/entities/CalendarEvent";

/**
 * TRE-2214 — copy for the client-facing "masz umówioną konsultację" banner.
 *
 * Only the positive state exists on purpose: no row means no banner, and there is deliberately no
 * "nie jesteś umówiona" counterpart, so the home screen never puts pressure on a client who has
 * not booked.
 *
 * Everything here is pure so both apps render the same sentence from the same row, and so the
 * date/plural/location branches are testable without a device.
 */

/** The slice of a `calendar_event` row the banner actually reads. */
export interface UpcomingConsultationRow {
  id: string;
  startTime: string;
  endTime: string;
  /** Raw Calendly `invitee.created` payload; only `scheduled_event` is read, defensively. */
  eventData: unknown;
  /**
   * Set by the trainer in admin when the call happens over a video link instead of the phone.
   * Overrides the link Calendly attached (Zoom), if any.
   */
  meetingUrl?: string | null;
}

/**
 * When the banner shows, tunable without a release through the payload of the PostHog flag
 * `consultation-banner` (a remote config flag). The defaults are the behaviour it shipped with:
 * visible from the moment she is booked until the call ends.
 *
 *   { "showHoursBefore": 48, "keepMinutesAfterEnd": 15 }
 */
export interface ConsultationBannerConfig {
  /** How long before the start the banner appears; null = as soon as she is booked. */
  showHoursBefore: number | null;
  /** How long it stays after the booked end, for a call that runs over. */
  keepMinutesAfterEnd: number;
}

export const CONSULTATION_BANNER_FLAG = "consultation-banner";

export const DEFAULT_CONSULTATION_BANNER_CONFIG: ConsultationBannerConfig = {
  showHoursBefore: null,
  keepMinutesAfterEnd: 0,
};

const readNonNegative = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null || raw === "") {
    return undefined;
  }

  const value = typeof raw === "number" ? raw : Number(raw);

  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

const safeJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * The payload is typed by hand in the PostHog UI, so every field falls back to its default on its
 * own rather than one typo discarding the whole config. `showHoursBefore: null` is a valid value
 * (no limit), distinct from a missing or broken one.
 */
export const parseConsultationBannerConfig = (payload: unknown): ConsultationBannerConfig => {
  const parsed = typeof payload === "string" ? safeJson(payload) : payload;

  if (typeof parsed !== "object" || parsed === null) {
    return DEFAULT_CONSULTATION_BANNER_CONFIG;
  }

  const { showHoursBefore, keepMinutesAfterEnd } = parsed as Record<string, unknown>;

  return {
    showHoursBefore:
      showHoursBefore === null
        ? null
        : (readNonNegative(showHoursBefore) ?? DEFAULT_CONSULTATION_BANNER_CONFIG.showHoursBefore),
    keepMinutesAfterEnd: readNonNegative(keepMinutesAfterEnd) ?? DEFAULT_CONSULTATION_BANNER_CONFIG.keepMinutesAfterEnd,
  };
};

/** Instants at which the banner for this row changes what it says, or appears / disappears. */
export const consultationBannerBoundaries = (
  row: Pick<UpcomingConsultationRow, "startTime" | "endTime">,
  config: ConsultationBannerConfig = DEFAULT_CONSULTATION_BANNER_CONFIG,
): number[] => {
  const start = dayjs(row.startTime);
  const end = dayjs(row.endTime);

  return [
    config.showHoursBefore === null ? null : start.subtract(config.showHoursBefore, "hour").valueOf(),
    start.valueOf(),
    end.valueOf(),
    end.add(config.keepMinutesAfterEnd, "minute").valueOf(),
  ].filter((instant): instant is number => instant !== null && Number.isFinite(instant));
};

export interface UpcomingConsultationDescription {
  /** The meeting has started but not ended: the banner talks in the present tense. */
  isLive: boolean;
  title: string;
  /** "dziś, 14:30" / "jutro, 14:30" / "czwartek, 25 września, 14:30"; end time while live. */
  when: string;
  /** Who runs the call, straight from the Calendly host, or null when the payload has no host. */
  host: string | null;
  /** How the call happens - a phone we call, a number she dials, a link, or "check your e-mail". */
  contact: string;
  /** "10 minut" - only meaningful before the call starts. */
  duration: string;
  /** The trainer's link, else Calendly's (Zoom); when set, the call happens there, not on the phone. */
  meetingUrl: string | null;
}

const WEEKDAY_WITH_DATE = "dddd, D MMMM";

const whenLabel = (start: dayjs.Dayjs, now: dayjs.Dayjs, t: TFunction): string => {
  const time = start.format("HH:mm");
  const daysAhead = start.startOf("day").diff(now.startOf("day"), "day");
  if (daysAhead <= 0) return t<string>("uiCommunication:consultation-today", { time });
  if (daysAhead === 1) return t<string>("uiCommunication:consultation-tomorrow", { time });
  return `${start.locale(t<string>("common:dateLocale")).format(WEEKDAY_WITH_DATE)}, ${time}`;
};

const readScheduledEvent = (eventData: unknown): Record<string, unknown> | null => {
  if (typeof eventData !== "object" || eventData === null) {
    return null;
  }

  const scheduled = (eventData as Record<string, unknown>).scheduled_event;

  return typeof scheduled === "object" && scheduled !== null ? (scheduled as Record<string, unknown>) : null;
};

/** The Calendly host, i.e. the trainer running the call. Membership order is the host order. */
const readHost = (eventData: unknown): string | null => {
  const memberships = readScheduledEvent(eventData)?.event_memberships;

  if (!Array.isArray(memberships) || memberships.length === 0) {
    return null;
  }

  const first = memberships[0];

  if (typeof first !== "object" || first === null) {
    return null;
  }

  const name = (first as Record<string, unknown>).user_name;

  return typeof name === "string" && name.trim().length > 0 ? name.trim() : null;
};

/**
 * 96% of the consultations on production are `outbound_call`: the trainer calls the client
 * (call2call), and `location.location` holds *her* number, so there is nothing to click and nothing
 * to join. `inbound_call` flips it - that number is the one she dials. Zoom and `custom` carry
 * either a nested Zoom object or a plain null, so rather than render a half-link we point at the
 * Calendly confirmation e-mail, which always has the real details.
 */
/**
 * Only an https link reaches a button: the apps hand this string straight to the browser, and the
 * database check is the one guard a row written before it cannot have passed.
 */
const readMeetingUrl = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const url = value.trim();

  return /^https:\/\/\S+$/i.test(url) ? url : null;
};

/**
 * The link Calendly itself put on the booking. On production (2026-09) every Zoom booking whose
 * meeting was created (`status: "pushed"`) carries `join_url`; a `failed` one has none. Google Meet,
 * Teams and Webex locations use the same key. A `custom` location is free text a trainer typed into
 * the event type, sometimes a pasted Zoom invitation with the meeting ID after the URL, so only the
 * first https URL in it is taken. Phone locations (93% of bookings) never have a link.
 */
export const readCalendlyMeetingUrl = (eventData: unknown): string | null => {
  const location = readScheduledEvent(eventData)?.location;

  if (typeof location !== "object" || location === null) {
    return null;
  }

  const { join_url: joinUrl, location: text } = location as Record<string, unknown>;
  const fromJoinUrl = readMeetingUrl(joinUrl);

  if (fromJoinUrl) {
    return fromJoinUrl;
  }

  const firstUrl = typeof text === "string" ? /https:\/\/\S+/i.exec(text)?.[0] : undefined;

  return readMeetingUrl(firstUrl);
};

/** The trainer's link from admin wins; otherwise whatever Calendly attached to the booking. */
const resolveMeetingUrl = (row: UpcomingConsultationRow): string | null =>
  readMeetingUrl(row.meetingUrl) ?? readCalendlyMeetingUrl(row.eventData);

const linkContact = (isLive: boolean, t: TFunction): string =>
  t<string>(isLive ? "uiCommunication:consultation-join-link" : "uiCommunication:consultation-scheduled-link");

const readContact = (eventData: unknown, t: TFunction): string => {
  const location = readScheduledEvent(eventData)?.location;

  if (typeof location !== "object" || location === null) {
    return t<string>("uiCommunication:consultation-contact-email");
  }

  const { type, location: value } = location as Record<string, unknown>;

  if (type === "outbound_call") {
    return t<string>("uiCommunication:consultation-outbound-call");
  }

  if (type === "inbound_call" && typeof value === "string" && value.trim().length > 0) {
    return t<string>("uiCommunication:consultation-inbound-call", { phone: value.trim() });
  }

  return t<string>("uiCommunication:consultation-contact-email");
};

/**
 * Turns a row into the sentence on the banner, or null when the meeting already ended - the query
 * filters those out, but a screen left open past `endTime` would otherwise keep showing a past hour.
 */
export const describeUpcomingConsultation = (
  row: UpcomingConsultationRow,
  t: TFunction,
  now: dayjs.ConfigType = undefined,
  config: ConsultationBannerConfig = DEFAULT_CONSULTATION_BANNER_CONFIG,
): UpcomingConsultationDescription | null => {
  const currentTime = dayjs(now);
  const start = dayjs(row.startTime);
  const end = dayjs(row.endTime);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const hideAt = end.add(config.keepMinutesAfterEnd, "minute");
  const showFrom = config.showHoursBefore === null ? null : start.subtract(config.showHoursBefore, "hour");

  if (!hideAt.isAfter(currentTime) || (showFrom && currentTime.isBefore(showFrom))) {
    return null;
  }

  const isLive = !start.isAfter(currentTime);
  const meetingUrl = resolveMeetingUrl(row);
  // Past the booked end, "do 10:10" would name an hour already gone, so the overrun window says
  // when it started instead.
  const liveWhen = end.isAfter(currentTime)
    ? t<string>("uiCommunication:consultation-until", { time: end.format("HH:mm") })
    : t<string>("uiCommunication:consultation-since", { time: start.format("HH:mm") });

  return {
    isLive,
    title: t<string>(isLive ? "uiCommunication:consultation-live" : "uiCommunication:consultation-scheduled"),
    when: isLive ? liveWhen : whenLabel(start, currentTime, t),
    host: readHost(row.eventData),
    contact: meetingUrl ? linkContact(isLive, t) : readContact(row.eventData, t),
    duration: t<string>("uiCommunication:consultation-minutes", { count: Math.max(1, end.diff(start, "minute")) }),
    meetingUrl,
  };
};

/** A `calendar_event` row as the calendar needs it: the banner slice plus what kind and how it went. */
export interface ConsultationEventRow extends UpcomingConsultationRow {
  type: string;
  status: string;
}

export interface ConsultationEntryDescription {
  title: string;
  /** "10:00 - 10:10", in the device's timezone. */
  timeRange: string;
  host: string | null;
  /** How the call happens - null once it is in the past, where the instruction is just noise. */
  contact: string | null;
  /** The trainer's video link, under the same rule as `contact`: only while it can still be used. */
  meetingUrl: string | null;
  /** Set only when the meeting did not simply happen as booked. */
  statusNote: string | null;
}

const CONSULTATION_TITLE: Record<string, string> = {
  [CalendarEventType.ONLINE_CONSULTATIONS]: "uiCommunication:consultation-online",
  [CalendarEventType.STATIONARY_CONSULTATIONS]: "uiCommunication:consultation-in-person",
};

/**
 * Only the outcomes worth naming. `active` gets nothing - a booked meeting needs no label, and a
 * past `active` row usually just means nobody updated the status afterwards, which is not something
 * to report to the client.
 */
const STATUS_NOTE: Record<string, string> = {
  [CalendarEventStatus.ATTENDED]: "uiCommunication:consultation-attended",
  [CalendarEventStatus.NO_SHOW]: "uiCommunication:consultation-missed",
  [CalendarEventStatus.CANCELED]: "uiCommunication:consultation-cancelled",
};

/**
 * TRE-2214 — one entry in the client's calendar, the counterpart of the trainer's "Zaplanowane
 * konsultacje". Shares the host and location readers with the banner, so the two never disagree on
 * who runs the call or how it happens.
 */
export const describeConsultationEvent = (
  row: ConsultationEventRow,
  t: TFunction,
  now: dayjs.ConfigType = undefined,
): ConsultationEntryDescription => {
  const start = dayjs(row.startTime);
  const end = dayjs(row.endTime);
  const isUsable = end.isAfter(dayjs(now)) && row.status === CalendarEventStatus.ACTIVE;
  const meetingUrl = isUsable ? resolveMeetingUrl(row) : null;

  return {
    title: t<string>(CONSULTATION_TITLE[row.type] ?? "uiCommunication:consultation"),
    timeRange: `${start.format("HH:mm")} - ${end.format("HH:mm")}`,
    host: readHost(row.eventData),
    contact: !isUsable ? null : meetingUrl ? linkContact(false, t) : readContact(row.eventData, t),
    meetingUrl,
    statusNote: STATUS_NOTE[row.status] ? t<string>(STATUS_NOTE[row.status]!) : null,
  };
};
